import { groupBy } from "lodash-es";
import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import { useRelationStore } from "@editor/items/relation/relations.store";

import type { WayItem } from "./WayItem";

const itemStructure: WayItem = {
  type: "way",

  title: "",

  startItemId: null,
  startItemType: "target",
  startItemChange: 0,

  relationIds: [],

  visible: false,
  complete: false,

  id: 0,
  uid: "way_0",
  docs: {},
  createdAt: -1
};

export const useWayStore = defineItemStore("model/ways", itemStructure, {
  state: () => ({
    activeId: null as number | null
  }),

  getters: {
    active(state) {
      const activeId = state.activeId;
      if (activeId == null) return null;
      return state.items[activeId];
    },
    byStartItem(state) {
      return groupBy(state.items, (w) => w.startItemId);
    },
    getLastItem() {
      return (wayId: number) => {
        const way = this.byId[wayId];
        if (way.startItemId == null) return null;
        if (way.relationIds.length == 0)
          return {
            id: way.startItemId,
            type: way.startItemType
          };
        const lastRelationId = way.relationIds[way.relationIds.length - 1];
        const lastRelation = useRelationStore().items[lastRelationId];
        return {
          id: lastRelation.endItemId,
          type: lastRelation.endItemType
        };
      };
    },
    getLastItemId() {
      return (wayId: number) => {
        const way = this.byId[wayId];
        if (way.startItemId == null) return null;
        if (way.relationIds.length == 0) return way.startItemId;
        const lastRelationId = way.relationIds[way.relationIds.length - 1];
        const lastRelation = useRelationStore().items[lastRelationId];
        return lastRelation.endItemId;
      };
    }
  },
  actions: {
    addItem({
      id,
      itemId,
      itemType
    }: {
      id: number;
      itemId: number;
      itemType: "target" | "resource";
    }) {
      const lastItem = this.getLastItem(id);
      if (lastItem == null) return false;

      const relationStore = useRelationStore();

      // get all relations that leave the last item:
      const relations = relationStore.orderedItems.filter(
        (relation) =>
          relation.updateType != "none" &&
          relation.endItemType == "target" &&
          relation.startItemType == lastItem.type &&
          relation.startItemId == lastItem.id
      );

      const relation = relations.find(
        (relation) =>
          relation.endItemId == itemId && relation.endItemType == itemType
      );
      if (!relation) return false;

      this.addRelation({
        id,
        relationId: relation.id
      });
    },

    setComplete({ id, complete }: { id: number; complete: boolean }) {
      const way = this.items[id];
      way.complete = complete;
    },

    setActive(id: number | null) {
      this.activeId = id;
    },

    /**
     *
     */
    setStartItem({
      id,
      itemId,
      itemType
    }: {
      id: number;
      itemId: number | null;
      itemType: WayItem["startItemType"];
    }) {
      const way = this.items[id];
      way.startItemId = itemId;
      way.startItemType = itemType;
    },

    setStartItemChange({
      id,
      startItemChange
    }: {
      id: number;
      startItemChange: number;
    }) {
      const way = this.items[id];
      way.startItemChange = startItemChange;
    },

    addRelation({ id, relationId }: { id: number; relationId: number }) {
      const way = this.items[id];
      way.relationIds.push(relationId);
    },

    removeLastRelation(wayToChange: { id: number }) {
      const id = wayToChange.id;
      const way = this.items[id];
      const relationIds = way.relationIds.slice(0, -1);
      way.relationIds = relationIds;
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useWayStore, import.meta.hot));
}
